import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories, site } from '@/mocks/guagua';

const FORM_URL = 'https://readdy.ai/api/form/dahao7rp14a1h3mr6h4g';

export default function SiteFooter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('company_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，今日份新鲜事会准时送到你的邮箱。');
      form.reset();
      setEmail('');
      return;
    }

    setStatus('loading');
    setMessage('');
    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const lowered = String(serverMsg).toLowerCase();
      const ok = response.ok && parsed?.code === 'OK' && !lowered.includes('spam');

      if (ok) {
        setStatus('success');
        setMessage('订阅成功，今日份新鲜事会准时送到你的邮箱。');
        form.reset();
        setEmail('');
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交未成功，请稍后再试一次。');
      }
    } catch {
      setStatus('error');
      setMessage('网络好像开小差了，请稍后再试一次。');
    }
  };

  return (
    <footer className="bg-background-950 text-background-100">
      <div className="px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <span className="flex items-center justify-center w-10 h-10 rounded-md bg-primary-500 text-background-50 text-lg">
                <i className="ri-emotion-laugh-line" aria-hidden="true"></i>
              </span>
              <span className="font-logo text-xl text-background-50">{site.name}</span>
            </Link>
            <p className="text-sm leading-relaxed text-background-300 max-w-md">{site.intro}</p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="guagua-newsletter-form"
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md"
            >
              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="newsletter-extra-field"
              />
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="留下邮箱，订阅今日份快讯"
                aria-label="邮箱地址"
                className="flex-1 px-4 py-2.5 rounded-md bg-background-900 border border-background-800 text-sm text-background-50 placeholder:text-background-500 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-1 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60"
              >
                {status === 'loading' ? '提交中' : '订阅'}
              </button>
            </form>
            {message && (
              <p
                className={`mt-3 text-sm ${
                  status === 'success' ? 'text-secondary-300' : 'text-primary-300'
                }`}
                role="status"
              >
                {message}
              </p>
            )}
          </div>

          <div>
            <h4 className="font-heading text-base text-background-50 mb-4">浏览栏目</h4>
            <ul className="space-y-2 text-sm">
              {categories.map((item) => (
                <li key={item.slug}>
                  <Link
                    to={`/news?cat=${item.slug}`}
                    className="text-background-300 hover:text-primary-300 transition-colors"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-base text-background-50 mb-4">关于本站</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/news" className="text-background-300 hover:text-primary-300 transition-colors">
                  新瓜速递
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-background-300 hover:text-primary-300 transition-colors">
                  关于瓜哥
                </Link>
              </li>
              <li>
                <Link to="/about#principles" className="text-background-300 hover:text-primary-300 transition-colors">
                  内容原则
                </Link>
              </li>
              <li>
                <a
                  href={`mailto:${site.email}`}
                  rel="nofollow"
                  className="text-background-300 hover:text-primary-300 transition-colors"
                >
                  {site.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-background-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-background-400">
          <p>© 2026 {site.name}. 内容仅供阅读参考，请理性吃瓜。</p>
          <p className="flex items-center gap-1">
            <i className="ri-heart-3-line" aria-hidden="true"></i>
            看得开心就好，别替别人下结论
          </p>
        </div>
      </div>
    </footer>
  );
}